import { apiFetch } from '../utils';
import { ACCOUNT_URL, SECURED_ACCOUNT_URL, SECURED_USERS_URL } from '../config';
import { RegistrationRequest } from '../auth/models/RegistrationRequest';
import { RegistrationRequestWithoutOrg } from '../auth/models/RegistrationRequestWithoutOrg';
import { withTokensIfNeeded } from '../utils/withTokensIfNeeded';

import { ValidationMethod } from './enums/ValidationMethod';
import { AuthResponse } from './models/AuthResponse';

interface RegistrationResponse extends AuthResponse {
    userId: string;
    oneTimeCode?: string;
}

interface RegistrationErrorResponseBody extends RegistrationResponse {
    result:
        | 'Success'
        | 'InvalidCountryCode'
        | 'ExistingAccount'
        | 'InvalidExternalRegistrationKey'
        | 'ExpiredExternalRegistrationKey'
        | 'UsedExternalRegistrationKey';
}

export const register = (registrationRequest: RegistrationRequest) => {
    const apiBody = {
        firstName: registrationRequest.firstName,
        lastName: registrationRequest.lastName,
        studentName: registrationRequest.studentName,
        fundraisingGroup: registrationRequest.fundraisingGroup,
        email: registrationRequest.email,
        username: registrationRequest.email,
        password: registrationRequest.password,
        organizationId: registrationRequest.organizationId,
        securityQuestionAnswers: registrationRequest.securityQuestionAnswers,
        marketingOptIn: registrationRequest.marketingOptIn,
        blackBoxValue: registrationRequest.blackBoxValue,
        createOneTimeCode: registrationRequest.createOneTimeCode,
        enrollSourceApplicationId: registrationRequest.enrollSourceApplicationId,
        enrollmentCode: registrationRequest.enrollmentCode,
        countryCode: registrationRequest.countryCode,
        externalRegistrationKey: registrationRequest.externalRegistrationKey,
        codeVerifier: registrationRequest.codeVerifier,
    };

    return apiFetch<RegistrationResponse, RegistrationErrorResponseBody>(
        `${ACCOUNT_URL}?api-version=1`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(apiBody),
        }
    );
};

export const registerWithoutOrg = (
    registrationRequest: Omit<
        RegistrationRequestWithoutOrg,
        'question1' | 'question2' | 'answer1' | 'answer2'
    >
) => {
    return apiFetch<RegistrationResponse>(`${ACCOUNT_URL}/without-org?api-version=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationRequest),
    });
};

export const getPasswordResetOptions = (userId: string) =>
    apiFetch<{ passwordResetOptions: ValidationMethod[] }>(
        `${ACCOUNT_URL}/${userId}/password/reset/options?api-version=1`
    );

export enum AccountRoles {
    FAMILY = 'Family',
    COORDINATOR = 'Coordinator',
    ASSISTANT_COORDINATOR = 'AssistantCoordinator',
}

export interface GetAccountResponse {
    username: string;
    email: string;
    lastPasswordUpdate: string | null;
    /** Linked OIDC provider id hint from account API (e.g. Google-RaiseRight). */
    externalLoginProvider?: string | null;
    hasPassword?: boolean;
    linkedSocialAccounts?: LinkedSocialAccountResponse[];
    organization: {
        organizationId: string;
        roleId: AccountRoles;
        isActive: boolean;
    };
}

export interface LinkedSocialAccountResponse {
    id: string;
    providerDisplayName: string;
    email: string | null;
}

export const getAccount = () =>
    withTokensIfNeeded<GetAccountResponse>(`${SECURED_ACCOUNT_URL}?api-version=1`);

/**
 * The account API may serialize fields as either camelCase or PascalCase, so callers
 * normalize through these helpers rather than reading a single casing directly.
 */
type RawAccountResponse = Record<string, unknown> & Partial<GetAccountResponse>;

const toLinkedSocialAccount = (entry: Record<string, unknown>): LinkedSocialAccountResponse => ({
    id: String(entry.id ?? entry.Id ?? ''),
    providerDisplayName: String(entry.providerDisplayName ?? entry.ProviderDisplayName ?? ''),
    email: (entry.email ?? entry.Email ?? null) as string | null,
});

/** Returns the linked social accounts from the account API, handling camel/Pascal casing. */
export const getLinkedSocialAccounts = (
    account?: GetAccountResponse | null
): LinkedSocialAccountResponse[] => {
    const data = account as RawAccountResponse | null | undefined;
    if (!data) {
        return [];
    }
    const camel = data.linkedSocialAccounts;
    const pascal = (data as Record<string, unknown>).LinkedSocialAccounts;
    const raw = Array.isArray(camel) ? camel : Array.isArray(pascal) ? pascal : [];
    return (raw as Array<Record<string, unknown>>)
        .map(toLinkedSocialAccount)
        .filter((entry) => Boolean(entry.id && entry.providerDisplayName));
};

/** Whether the account has a password set, handling camel/Pascal casing with a legacy fallback. */
export const getAccountHasPassword = (account?: GetAccountResponse | null): boolean => {
    const data = account as RawAccountResponse | null | undefined;
    return (
        data?.hasPassword ??
        ((data as Record<string, unknown>)?.HasPassword as boolean | undefined) ??
        Boolean(data?.lastPasswordUpdate)
    );
};

export type RemoveLinkedSocialLoginResult =
    | 'Success'
    | 'InvalidLinkedAccountId'
    | 'NotFound'
    | 'PasswordRequired'
    | 'Failed';

export interface RemoveLinkedSocialLoginResponse {
    result?: RemoveLinkedSocialLoginResult;
    errorCode?: string;
    errorMessage?: string;
    Result?: RemoveLinkedSocialLoginResult;
    ErrorCode?: string;
    ErrorMessage?: string;
}

export const removeLinkedSocialLogin = (id: string) =>
    withTokensIfNeeded<RemoveLinkedSocialLoginResponse, RemoveLinkedSocialLoginResponse>(
        `${SECURED_ACCOUNT_URL}/social-login/${encodeURIComponent(id)}?api-version=1`,
        { method: 'DELETE' }
    );

export const changePassword = (currentPassword: string, newPassword: string) =>
    withTokensIfNeeded(`${SECURED_ACCOUNT_URL}/password?api-version=1`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
    });

interface TokenExchangeResponse {
    accessToken: string;
}

export const getTravelSsoParams = async () => {
    return withTokensIfNeeded<TokenExchangeResponse>(
        `${SECURED_USERS_URL}/token/exchange?api-version=1`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );
};
